import { useEffect, useMemo, useState } from "react";
import { Button, SectionTitle } from "@/components/ui";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

interface Template {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  isBuiltin: boolean;
  cardCount: string | null;
}

/** Modal gallery. `intoBoardId` fills the current empty board; otherwise a new sub-board is created under `parentBoardId`. */
export function TemplateGallery({
  intoBoardId,
  parentBoardId,
  onApplied,
  onClose,
}: {
  intoBoardId?: string;
  parentBoardId?: string;
  onApplied: (boardId: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [list, setList] = useState<Template[] | null>(null);
  const [cat, setCat] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [keep, setKeep] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    api<Template[]>("/api/templates").then(setList);
  }, []);
  const cats = useMemo(() => [...new Set((list ?? []).map((x) => x.category ?? "—"))], [list]);
  const shown = (list ?? []).filter(
    (x) =>
      (!cat || (x.category ?? "—") === cat) &&
      (!q ||
        x.name.toLowerCase().includes(q.toLowerCase()) ||
        (x.description ?? "").toLowerCase().includes(q.toLowerCase())),
  );
  const apply = async (tpl: Template) => {
    setBusy(tpl.id);
    try {
      const r = await api<{ boardId: string }>(`/api/templates/${tpl.id}/apply`, {
        method: "POST",
        json: { intoBoardId, parentBoardId: intoBoardId ? undefined : parentBoardId, keepContent: keep },
      });
      onApplied(r.boardId);
    } finally {
      setBusy(null);
    }
  };
  const remove = async (tpl: Template) => {
    if (!confirm(t.confirmDeleteTemplate(tpl.name))) return;
    await api(`/api/templates/${tpl.id}`, { method: "DELETE" });
    setList((l) => (l ?? []).filter((x) => x.id !== tpl.id));
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/70 p-4 pt-12"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-4xl rounded-md border border-border bg-panel p-6 shadow-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal
      >
        <div className="flex items-start justify-between gap-4">
          <SectionTitle>{t.chooseTemplate}</SectionTitle>
          <button type="button" onClick={onClose} className="text-muted hover:text-text">
            ✕
          </button>
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.searchTemplates}
            className="min-w-48 rounded-sm border border-border bg-panel-2 px-3 py-1.5 text-sm outline-none"
          />
          <button
            type="button"
            onClick={() => setCat(null)}
            className={`rounded-full px-3 py-1 text-xs ${cat === null ? "bg-accent text-bg" : "bg-panel-2 text-muted"}`}
          >
            {t.all}
          </button>
          {cats.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCat(c)}
              className={`rounded-full px-3 py-1 text-xs ${cat === c ? "bg-accent text-bg" : "bg-panel-2 text-muted"}`}
            >
              {c}
            </button>
          ))}
          <label className="ml-auto flex items-center gap-2 text-sm text-muted">
            <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} />{" "}
            {t.keepExampleContent}
          </label>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {intoBoardId ? (
            <button
              type="button"
              onClick={onClose}
              className="flex min-h-36 flex-col items-start rounded-md border border-dashed border-border p-4 text-left hover:border-text"
            >
              <span className="display text-xl">.{t.emptyBoardTpl}</span>
              <span className="mt-1 text-sm text-muted">{t.emptyBoardTplHint}</span>
            </button>
          ) : null}
          {shown.map((tpl) => (
            <div
              key={tpl.id}
              className="flex min-h-36 flex-col rounded-md border border-border bg-panel-2 p-4"
            >
              <div className="text-[11px] uppercase tracking-wide text-dim">{tpl.category ?? "—"}</div>
              <div className="display mt-1 text-xl">.{tpl.name}</div>
              <p className="mt-1 flex-1 text-sm text-muted">{tpl.description}</p>
              <div className="mt-3 flex items-center gap-2">
                <Button className="px-3 py-1.5 text-xs" onClick={() => apply(tpl)} loading={busy === tpl.id}>
                  {t.useTemplate}
                </Button>
                <span className="text-[11px] text-dim">
                  {tpl.cardCount} {t.cards}
                </span>
                {!tpl.isBuiltin ? (
                  <button
                    type="button"
                    onClick={() => remove(tpl)}
                    className="ml-auto text-[11px] text-dim hover:text-error"
                  >
                    {t.remove}
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
